import { useState, useEffect, useCallback } from 'react';
import {
  Check,
  X,
  Clock,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Search,
  Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { withdrawalsAPI, transactionsAPI, depositsAPI, financeAPI } from '../lib/api';
import { formatCurrency, formatDateTime, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  return (
    <span className={`badge ${colorClasses[color] || 'badge-neutral'}`}>
      {(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
};

const clean = (value) => (!value || value === 'all' ? undefined : value);

const txId = (tx) => tx.txn_id || tx.id || tx.reference_id || '—';

export default function WalletPayments() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('actionable');
  const [wdStatus, setWdStatus] = useState('pending');
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('today');
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectKind, setRejectKind] = useState('withdrawal');
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [shot, setShot] = useState(null);
  const [activeTab, setActiveTab] = useState('deposits');

  const fetchSummary = useCallback(async () => {
    try {
      const res = await financeAPI.getSummary(period);
      setSummary(res.data);
    } catch {
      setSummary(null);
    }
  }, [period]);

  const fetchDeposits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await depositsAPI.getAll({
        skip: page * limit,
        limit,
        status: clean(statusFilter),
        search: search.trim() || undefined,
      });
      setDeposits(response.data.deposits || []);
      setTotalDeposits(response.data.total || 0);
    } catch {
      toast.error('Failed to fetch deposits');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await withdrawalsAPI.getAll({
        skip: page * limit,
        limit,
        status: clean(wdStatus),
        search: search.trim() || undefined,
      });
      setWithdrawals(response.data.withdrawals || []);
      setTotalWithdrawals(response.data.total || 0);
    } catch {
      toast.error('Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  }, [page, limit, wdStatus, search]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await transactionsAPI.getAll({
        skip: page * limit,
        limit,
        type: clean(txTypeFilter),
        search: search.trim() || undefined,
      });
      setTransactions(response.data.transactions || []);
      setTotalTransactions(response.data.total || 0);
    } catch {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, txTypeFilter, search]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (activeTab === 'deposits') fetchDeposits();
    else if (activeTab === 'withdrawals') fetchWithdrawals();
    else fetchTransactions();
  }, [activeTab, fetchDeposits, fetchWithdrawals, fetchTransactions]);

  const refresh = () => {
    fetchSummary();
    if (activeTab === 'deposits') fetchDeposits();
    else if (activeTab === 'withdrawals') fetchWithdrawals();
    else fetchTransactions();
  };

  const handleApproveDeposit = async (id) => {
    try {
      await depositsAPI.approve(id);
      toast.success('Deposit credited to wallet');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve deposit');
    }
  };

  const handleApproveWithdrawal = async (id) => {
    try {
      await withdrawalsAPI.approve(id);
      toast.success('Withdrawal approved');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve withdrawal');
    }
  };

  const openReject = (kind, row) => {
    setRejectKind(kind);
    setSelected(row);
    setRejectReason('');
    setRejectDialog(true);
  };

  const handleReject = async () => {
    try {
      const id = rejectKind === 'deposit'
        ? selected.deposit_id
        : (selected.id || selected.withdrawal_id);
      if (rejectKind === 'deposit') {
        await depositsAPI.reject(id, rejectReason);
        toast.success('Deposit rejected');
      } else {
        await withdrawalsAPI.reject(id, rejectReason);
        toast.success('Withdrawal rejected and refunded');
      }
      setRejectDialog(false);
      setSelected(null);
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const openScreenshot = async (depositId) => {
    try {
      const res = await depositsAPI.getById(depositId);
      const raw = res.data.screenshot_url || '';
      if (!raw) {
        toast.error('No screenshot uploaded');
        return;
      }
      setShot(raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`);
    } catch {
      toast.error('Could not load screenshot');
    }
  };

  const totalForTab = activeTab === 'deposits'
    ? totalDeposits
    : activeTab === 'withdrawals'
      ? totalWithdrawals
      : totalTransactions;

  return (
    <div data-testid="wallet-page">
      <div className="page-header flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Deposits & Withdrawals</h1>
          <p className="page-subtitle">Review UPI deposits, approve payouts, and search the ledger</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="7days">Last 7 days</SelectItem>
            <SelectItem value="30days">Last 30 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="widget-grid mb-6">
        <div className="stat-card">
          <div className="widget-icon bg-amber-500/10 text-amber-400"><Clock className="w-5 h-5" /></div>
          <p className="stat-value mt-3">{summary?.pending_deposits ?? 0}</p>
          <p className="stat-label">Deposits to review</p>
          <p className="text-xs text-muted-foreground mt-1">{formatCurrency(summary?.pending_deposit_amount || 0)}</p>
        </div>
        <div className="stat-card">
          <div className="widget-icon bg-emerald-500/10 text-emerald-400"><ArrowDownLeft className="w-5 h-5" /></div>
          <p className="stat-value mt-3">{formatCurrency(summary?.deposits || 0)}</p>
          <p className="stat-label">Approved deposits</p>
        </div>
        <div className="stat-card">
          <div className="widget-icon bg-red-500/10 text-red-400"><ArrowUpRight className="w-5 h-5" /></div>
          <p className="stat-value mt-3">{formatCurrency(summary?.withdrawals || 0)}</p>
          <p className="stat-label">Approved withdrawals</p>
          <p className="text-xs text-muted-foreground mt-1">{summary?.pending_withdrawals || 0} pending · {formatCurrency(summary?.pending_withdrawal_amount || 0)}</p>
        </div>
        <div className="stat-card">
          <div className="widget-icon bg-primary/10 text-primary"><DollarSign className="w-5 h-5" /></div>
          <p className="stat-value mt-3">{formatCurrency(summary?.net_flow || 0)}</p>
          <p className="stat-label">Net flow</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="deposits" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <ArrowDownLeft className="w-4 h-4 mr-2" />
            Deposits
          </TabsTrigger>
          <TabsTrigger value="withdrawals" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Wallet className="w-4 h-4 mr-2" />
            Withdrawals
          </TabsTrigger>
          <TabsTrigger value="transactions" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CreditCard className="w-4 h-4 mr-2" />
            Ledger
          </TabsTrigger>
        </TabsList>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search phone, user id, or reference"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </div>
          {activeTab === 'deposits' && (
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="actionable">Needs review</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          )}
          {activeTab === 'withdrawals' && (
            <Select value={wdStatus} onValueChange={(v) => { setWdStatus(v); setPage(0); }}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          )}
          {activeTab === 'transactions' && (
            <Select value={txTypeFilter} onValueChange={(v) => { setTxTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
                <SelectItem value="withdrawal">Withdrawal</SelectItem>
                <SelectItem value="entry_fee">Entry fee</SelectItem>
                <SelectItem value="settlement">Settlement</SelectItem>
                <SelectItem value="rake">Rake</SelectItem>
                <SelectItem value="bonus">Bonus</SelectItem>
                <SelectItem value="refund">Refund</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        <TabsContent value="deposits">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Player</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Proof</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></TableCell></TableRow>
                ) : deposits.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No deposits found</TableCell></TableRow>
                ) : deposits.map((d) => (
                  <TableRow key={d.deposit_id}>
                    <TableCell>
                      <p className="font-medium">{d.phone || '—'}</p>
                      <p className="text-xs text-muted-foreground font-mono">{d.user_id}</p>
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(d.amount)}</TableCell>
                    <TableCell className="capitalize">{d.payment_method || 'upi'}</TableCell>
                    <TableCell>
                      {d.has_screenshot ? (
                        <Button size="sm" variant="outline" onClick={() => openScreenshot(d.deposit_id)}>
                          <ImageIcon className="w-4 h-4 mr-1" /> View
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{formatDateTime(d.created_at)}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      {(d.status === 'pending' || d.status === 'under_review') && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" className="text-emerald-400" onClick={() => handleApproveDeposit(d.deposit_id)}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-400" onClick={() => openReject('deposit', d)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="withdrawals">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Player</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></TableCell></TableRow>
                ) : withdrawals.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No withdrawals found</TableCell></TableRow>
                ) : withdrawals.map((w) => {
                  const id = w.id || w.withdrawal_id;
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <p className="font-medium">{w.user_name || w.phone || '—'}</p>
                        <p className="text-xs text-muted-foreground font-mono">{w.user_id}</p>
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(w.amount)}</TableCell>
                      <TableCell className="text-sm">
                        {w.bank_name || w.bank_details?.bank_name || '—'}
                        {(w.account_number || w.bank_details?.account) && (
                          <span className="block text-xs text-muted-foreground">···· {w.account_number || w.bank_details?.account}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(w.created_at)}</TableCell>
                      <TableCell><StatusBadge status={w.status} /></TableCell>
                      <TableCell className="text-right">
                        {w.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" className="text-emerald-400" onClick={() => handleApproveWithdrawal(id)}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-400" onClick={() => openReject('withdrawal', w)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Reference</TableHead>
                  <TableHead>Player</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" /></TableCell></TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No transactions found</TableCell></TableRow>
                ) : transactions.map((tx, i) => (
                  <TableRow key={txId(tx) + i}>
                    <TableCell className="font-mono text-xs">{String(txId(tx)).slice(0, 14)}</TableCell>
                    <TableCell className="text-sm">{tx.phone || tx.user_id || '—'}</TableCell>
                    <TableCell className="capitalize">{tx.type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">{tx.description || '—'}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(tx.amount)}</TableCell>
                    <TableCell><StatusBadge status={tx.status || 'completed'} /></TableCell>
                    <TableCell className="text-sm">{formatDateTime(tx.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between px-1 py-3">
        <p className="text-sm text-muted-foreground">
          Showing {totalForTab === 0 ? 0 : page * limit + 1} to {Math.min((page + 1) * limit, totalForTab)} of {totalForTab}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= totalForTab}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject {rejectKind === 'deposit' ? 'deposit' : 'withdrawal'}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {rejectKind === 'withdrawal'
                ? `This refunds ${formatCurrency(selected?.amount || 0)} to winnings.`
                : 'The player will not be credited.'}
            </p>
            <Textarea placeholder="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={4} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={!rejectReason.trim()}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shot} onOpenChange={() => setShot(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Payment screenshot</DialogTitle></DialogHeader>
          {shot && <img src={shot} alt="Deposit proof" className="w-full rounded-lg border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
